import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { logger } from "../lib/logger";
import { ArtworkCard } from "../components/ArtworkCard";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Seo, { breadcrumbLd } from "../components/Seo";
import { Home, Image as ImageIcon } from "lucide-react";

const MEDIUMS = ["All", "Painting", "Mixed Media", "Sculpture", "Photography", "Digital"];
const IN_SITU_STORAGE_KEY = "aam_gallery_view_mode";

export default function Gallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [medium, setMedium] = useState(searchParams.get("medium") || "All");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [priceRange, setPriceRange] = useState("All");
  // Persisted toggle: "clean" = raw thumbnails, "insitu" = framed in a room.
  // We remember the choice in localStorage so returning buyers land in the
  // mode they prefer without having to click again on every visit.
  const [inSitu, setInSitu] = useState(() => {
    try {
      return localStorage.getItem(IN_SITU_STORAGE_KEY) === "insitu";
    } catch (e) {
      logger.warn("localStorage read blocked (private mode?)", e);
      return false;
    }
  });

  const toggleViewMode = () => {
    setInSitu((v) => {
      const next = !v;
      try {
        localStorage.setItem(IN_SITU_STORAGE_KEY, next ? "insitu" : "clean");
      } catch (e) {
        // localStorage can be blocked in private/incognito mode — non-fatal.
        logger.warn("localStorage write blocked (private mode?)", e);
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchArtworks = async () => {
      setLoading(true);
      const params = {};
      if (medium !== "All") params.medium = medium;
      if (search) params.search = search;
      if (priceRange === "under-2000") params.max_price = 2000;
      if (priceRange === "2000-3000") {
        params.min_price = 2000;
        params.max_price = 3000;
      }
      if (priceRange === "above-3000") params.min_price = 3000;
      try {
        const { data } = await api.get("/artworks", { params });
        setArtworks(data);
      } catch (err) {
        logger.error("Gallery list load failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArtworks();
  }, [medium, search, priceRange]);

  return (
    <div data-testid="gallery-page" className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
      <Seo
        title="Gallery — Original African Contemporary Art"
        description="Browse the full collection of original paintings, mixed-media works, sculpture and photography from contemporary African masters. Direct from the artist's studio."
        path="/gallery"
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Gallery", path: "/gallery" },
        ])}
      />
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
        <div>
          <p className="label-eyebrow text-[#555]">— The Collection</p>
          <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter leading-none font-light mt-4">
            Gallery
          </h1>
          <p className="text-[#555] mt-3 font-mono-aam text-xs">{artworks.length} works on view</p>
        </div>

        {/* View mode toggle (v1.28) — flips between clean thumbnails and
            framed-in-a-room mockups. Persisted in localStorage. */}
        <div
          data-testid="view-mode-toggle"
          className="inline-flex items-stretch border border-[#E5E0D8] bg-white"
        >
          <button
            type="button"
            onClick={() => inSitu && toggleViewMode()}
            data-testid="view-mode-clean"
            aria-pressed={!inSitu}
            className={`flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider transition ${
              !inSitu ? "bg-[#111] text-white" : "text-[#555] hover:text-[#111]"
            }`}
          >
            <ImageIcon className="w-3.5 h-3.5" />
            Clean grid
          </button>
          <button
            type="button"
            onClick={() => !inSitu && toggleViewMode()}
            data-testid="view-mode-insitu"
            aria-pressed={inSitu}
            className={`flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider transition ${
              inSitu ? "bg-[#111] text-white" : "text-[#555] hover:text-[#111]"
            }`}
          >
            <Home className="w-3.5 h-3.5" />
            In situ
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 pb-8 border-b border-[#E5E0D8]">
        <Input
          data-testid="search-input"
          placeholder="Search by title, artist, or keyword…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
        />
        <Select value={medium} onValueChange={setMedium}>
          <SelectTrigger
            data-testid="medium-filter"
            className="rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
          >
            <SelectValue placeholder="Medium" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {MEDIUMS.map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priceRange} onValueChange={setPriceRange}>
          <SelectTrigger
            data-testid="price-filter"
            className="rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
          >
            <SelectValue placeholder="Price" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="All">All prices</SelectItem>
            <SelectItem value="under-2000">Under $2,000</SelectItem>
            <SelectItem value="2000-3000">$2,000 – $3,000</SelectItem>
            <SelectItem value="above-3000">Above $3,000</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="label-eyebrow text-[#555]">Loading the gallery…</p>
      ) : artworks.length === 0 ? (
        <p className="font-serif-display text-3xl text-[#555]">No works match your search.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-14">
          {artworks.map((art, idx) => (
            <ArtworkCard
              key={art.id}
              artwork={art}
              aspect="square"
              inSitu={inSitu}
              sceneIndex={idx}
            />
          ))}
        </div>
      )}
    </div>
  );
}
